/** Title (email only), assign, snooze, mark reviewed and archive for one conversation (admins and operators only). */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../../api/client'
import { TITLE_LABELS, TITLE_ORDER, type DeskConversation, type DeskPatch, type DeskTitle, type StaffMember } from '../../lib/supportDesk'
import { earlierHref, staffMeFirst } from '../../lib/supportWorkspace'
import { apiErrorText } from '../../lib/apiErrorText'

const SELECT = 'bg-ink-900 border border-ink-500 px-2 py-2 text-paper font-mono text-[12px]'
const BUTTON = 'label border border-ink-500 px-3 py-2 hover:border-amber-glow disabled:opacity-40'

type Props = { conv: DeskConversation; staff: StaffMember[]; meEmail: string | undefined }

export function ConversationActions({ conv, staff, meEmail }: Props) {
  const qc = useQueryClient()
  const patch = useMutation({
    mutationFn: (body: DeskPatch) => api.patch<DeskConversation>(`/api/support/conversations/${conv.channel}/${conv.message_id}`, body),
    onSettled: () => qc.invalidateQueries({ queryKey: ['support'] }),
  })
  const navigate = useNavigate()
  // Retitling moves the conversation to another bucket: follow it there, still open.
  const retitle = (publication: DeskTitle) => patch.mutate({ publication }, {
    onSuccess: () => {
      // The reply's "sent as" brand follows the title.
      qc.invalidateQueries({ queryKey: ['reply-thread', conv.channel, conv.message_id] })
      navigate(earlierHref({ channel: conv.channel, id: conv.message_id, publication }))
    },
  })
  const busy = patch.isPending
  const snooze = (days: number) => patch.mutate({ snoozed_until: new Date(Date.now() + days * 86_400_000).toISOString() })
  const people = staffMeFirst(staff, meEmail)

  return (
    <div className="flex flex-wrap items-center gap-2">
      {conv.channel === 'email' && (
        <>
          <label className="sr-only" htmlFor="conv-title">Title</label>
          <select id="conv-title" className={SELECT} disabled={busy} value={conv.publication ?? ''}
            onChange={e => { if (e.target.value) retitle(e.target.value as DeskTitle) }}>
            {!conv.publication && <option value="">Choose a title…</option>}
            {TITLE_ORDER.map(t => <option key={t} value={t}>{TITLE_LABELS[t]}</option>)}
          </select>
        </>
      )}
      <label className="sr-only" htmlFor="assign-to">Assign to</label>
      <select id="assign-to" className={SELECT} disabled={busy} value={conv.assigned_to_user_id ?? ''}
        onChange={e => patch.mutate({ assignee_user_id: e.target.value === '' ? null : Number(e.target.value) })}>
        <option value="">Unassigned</option>
        {people.map(s => (
          <option key={s.id} value={s.id}>{s.email.toLowerCase() === meEmail?.toLowerCase() ? `Me (${s.email})` : s.email}</option>
        ))}
      </select>
      <label className="sr-only" htmlFor="snooze">Snooze</label>
      <select id="snooze" className={SELECT} disabled={busy} value=""
        onChange={e => { const v = e.target.value; if (v === 'off') patch.mutate({ snoozed_until: null }); else if (v) snooze(Number(v)) }}>
        <option value="">{conv.state === 'snoozed' ? 'Snoozed…' : 'Snooze…'}</option>
        <option value="1">1 day</option>
        <option value="3">3 days</option>
        <option value="7">1 week</option>
        {conv.state === 'snoozed' && <option value="off">Stop snoozing</option>}
      </select>
      {conv.status === 'new' && (
        <button type="button" className={BUTTON} disabled={busy} onClick={() => patch.mutate({ status: 'read' })}>Mark reviewed</button>
      )}
      {conv.status !== 'archived' && (
        <button type="button" className={BUTTON} disabled={busy} onClick={() => patch.mutate({ status: 'archived' })}>Archive</button>
      )}
      {patch.isError && <span className="text-red-400 text-sm">{apiErrorText(patch.error)}</span>}
    </div>
  )
}
